import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Purchases } from "@revenuecat/purchases-js";
import { auth } from "../firebase/firebase.utils";
import { setEntitlement as storeEntitlement } from "../redux/user/user.actions";

export default function useEntitlement() {
  const uid = useSelector((state) => state.user.currentUser?.uid);
  const dispatch = useDispatch();
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    let running = false;
    setEntitlement(null);
    setLoading(true);
    dispatch(storeEntitlement("free"));

    async function refresh() {
      if (running || document.visibilityState === "hidden") return;
      if (!uid) { setLoading(false); return; }
      running = true;
      try {
        // App.js keeps responsibility for configuring RevenueCat and changing its user.
        const purchases = Purchases.getSharedInstance();
        if (purchases.getAppUserId() !== uid || auth.currentUser?.uid !== uid) return;
        const info = await purchases.getCustomerInfo();
        if (!live || auth.currentUser?.uid !== uid || purchases.getAppUserId() !== uid) return;
        const active = info.entitlements.active;
        const next = active.antBusiness ? "antBusiness" : active.antPro ? "antPro" : "free";
        dispatch(storeEntitlement(next));
        // Preserve the original hook's public return values.
        setEntitlement(next === "antBusiness" ? "business" : next === "antPro" ? "pro" : "free");
        setLoading(false);
      } catch (error) {
        // A network error is not proof that paid access was revoked. Retry below.
        if (live) setLoading(false);
      } finally { running = false; }
    }

    refresh();
    const initialRetry = setTimeout(refresh, 1500);
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    window.addEventListener("antlogy:membership-refresh", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      clearTimeout(initialRetry);
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("antlogy:membership-refresh", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [uid, dispatch]);

  return { entitlement, loading };
}
